import { UcoastEl } from '@/scripts/core/UcoastEl'
import { qsOptional, qsaOptional } from '@/scripts/core/global'

export class AnnouncementBar extends UcoastEl {
	static htmlSelector = 'announcement-bar'
	// Must match $announcementSlideDuration in announcement-bar.scss.
	static transitionMs = 500
	static defaultSpeedSeconds = 5

	show: boolean
	el?: HTMLElement
	height?: number
	slides: HTMLElement[]
	activeIndex = 0
	intervalMs: number
	timer?: ReturnType<typeof setInterval>
	leavingTimeout?: ReturnType<typeof setTimeout>
	resizeObserver?: ResizeObserver
	boundPause = () => this.pause()
	boundResume = () => this.resume()
	boundVisibility = () => (document.hidden ? this.pause() : this.resume())

	constructor() {
		super()
		this.show = this.getAttribute('data-show') === 'true'
		this.el = qsOptional('.announcement-bar', this)
		this.slides = Array.from(qsaOptional('.announcement-bar__slide', this) ?? [])

		const speed = parseFloat(this.getAttribute('data-speed') ?? '')
		this.intervalMs =
			(Number.isFinite(speed) && speed > 0 ? speed : AnnouncementBar.defaultSpeedSeconds) * 1000

		this.setAnnouncementHeight()
		if (!this.show || !this.el) {
			return
		}

		this.resizeObserver = new ResizeObserver(() => this.setAnnouncementHeight())
		this.resizeObserver.observe(this.el)

		// One message behaves exactly as it did before: no timer, no listeners.
		if (this.slides.length < 2) return

		this.addEventListener('mouseenter', this.boundPause)
		this.addEventListener('mouseleave', this.boundResume)
		this.addEventListener('focusin', this.boundPause)
		this.addEventListener('focusout', this.boundResume)
		document.addEventListener('visibilitychange', this.boundVisibility)

		this.resume()
	}

	setAnnouncementHeight() {
		this.height = this.el ? this.el.offsetHeight : 0
		document.documentElement.style.setProperty('--bar-height', `${this.height}px`)
	}

	resume() {
		if (this.timer || this.slides.length < 2 || document.hidden) return
		this.timer = setInterval(() => this.next(), this.intervalMs)
	}

	pause() {
		if (!this.timer) return
		clearInterval(this.timer)
		this.timer = undefined
	}

	next() {
		const current = this.slides[this.activeIndex]
		const nextIndex = (this.activeIndex + 1) % this.slides.length
		const upcoming = this.slides[nextIndex]
		if (!current || !upcoming) return

		current.classList.remove('announcement-bar__slide--active')
		current.classList.add('announcement-bar__slide--leaving')
		upcoming.classList.add('announcement-bar__slide--active')
		this.activeIndex = nextIndex

		// The leaving slide stays visible while it fades, which would otherwise
		// leave its link tabbable for half a second after it stopped being the
		// message on screen. Resting slides are visibility:hidden, so they drop out
		// of the tab order on their own.
		current.setAttribute('tabindex', '-1')
		upcoming.removeAttribute('tabindex')

		// Drop the leaving class once it has finished animating out, so the slide
		// falls back to the waiting-below state ready for its next turn.
		if (this.leavingTimeout) clearTimeout(this.leavingTimeout)
		this.leavingTimeout = setTimeout(() => {
			current.classList.remove('announcement-bar__slide--leaving')
		}, AnnouncementBar.transitionMs)
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.pause()
		if (this.leavingTimeout) clearTimeout(this.leavingTimeout)
		this.resizeObserver?.disconnect()
		this.removeEventListener('mouseenter', this.boundPause)
		this.removeEventListener('mouseleave', this.boundResume)
		this.removeEventListener('focusin', this.boundPause)
		this.removeEventListener('focusout', this.boundResume)
		document.removeEventListener('visibilitychange', this.boundVisibility)
	}
}
